import { createFileRoute } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { FileText, Users, Video } from 'lucide-react';
import { Button } from '@worldcoo/ui/components/button';
import { ExportIcon } from '@worldcoo/ui/custom/export-icon';
import type { Locale } from '@worldcoo/monolith/core';
import { MainSectionHeader, MainSectionContent } from './-components/main-section';

export const Route = createFileRoute('/ngos/resources')({
	component: ResourcesPage,
});

type ResourceVariant = 'es' | 'ca' | 'en' | 'it';

type LocalizedLinks = Partial<Record<ResourceVariant, string>> & { es: string };

const FAQS_LINKS: LocalizedLinks = {
	es: 'https://docs.google.com/document/d/1Qk4cfNsJKD3Ms41odbXfiLjbOJ3fPsADZ049L5TDj_g/edit?usp=drive_link',
	ca: 'https://docs.google.com/document/d/1Qk4cfNsJKD3Ms41odbXfiLjbOJ3fPsADZ049L5TDj_g/edit?usp=drive_link',
	it: 'https://docs.google.com/document/d/1QyycScQqQ0qt_ArKtNi2mIimzn_4Xj-qxJ5Q3fuSzkg/edit?usp=drive_link',
};

const COMMS_PLAYBOOK_LINKS: LocalizedLinks = {
	es: 'https://drive.google.com/file/d/1MZt_NMQJ54_a4u5t7QswIRUdmRUHEs0u/view?usp=drive_link',
	ca: 'https://drive.google.com/file/d/1cGJ4sVwIioLUNb17r-48P4Wci-DetWY6/view?usp=drive_link',
};

const COMMS_REEL_PLAYBOOK_LINKS: LocalizedLinks = {
	es: 'https://drive.google.com/file/d/1fKHRWArVOgkHhcs1KldZOK7kxRA-LZmW/view?usp=drive_link',
	ca: 'https://drive.google.com/file/d/1jD5o1N0WBjG6g0v3YX9A7n0HQvAYDrMY/view?usp=drive_link',
	it: 'https://drive.google.com/file/d/1_JSBXVcmFJWWlrHS04BEyR07UJsod0ea/view?usp=drive_link',
};

const REPORT_PLAYBOOK_LINKS: LocalizedLinks = {
	es: 'https://docs.google.com/document/d/1raz0rDNErF490SHPJEQQ-hdOisvm59Lnt6t40t3HWCc/edit?usp=drive_link',
	ca: 'https://docs.google.com/document/d/1YwdX_r4UqVUKSErxDgLV5t7VDjFIV_GJnijSvv8TIUI/edit?tab=t.0',
	en: 'https://docs.google.com/document/d/15pLeIKruaEo5CICEUQhLXQCo8WoBuezG4Cd5SNZ1Qz0/edit?usp=drive_link',
	it: 'https://docs.google.com/document/d/1oXcMh29eyJF1jJ3KqMYZF5UIVIYVut2r7y2d5hiup9I/edit?usp=drive_link',
};

const BRING_A_PARTNER_FORM_LINK = 'https://tally.so/r/nGbNxZ';

function localeToVariant(locale: Locale): ResourceVariant {
	switch (locale) {
		case 'ca':
			return 'ca';
		case 'it':
			return 'it';
		case 'en':
		case 'fr':
			return 'en';
		default:
			return 'es';
	}
}

function pickLink(links: LocalizedLinks, variant: ResourceVariant): string {
	return links[variant] ?? links.en ?? links.es;
}

function ResourcesPage() {
	const { t, i18n } = useLingui();
	const locale = (i18n.locale || 'en') as Locale;
	const variant = localeToVariant(locale);

	const items: Array<{
		key: string;
		title: string;
		icon: React.ReactNode;
		href: string;
		ctaLabel: string;
	}> = [
		{
			key: 'faqs',
			title: t`Frequently asked questions about working with Worldcoo`,
			icon: <FileText className="size-6 text-[#6b4fa3]" strokeWidth={1.5} />,
			href: pickLink(FAQS_LINKS, variant),
			ctaLabel: t`Open document`,
		},
		{
			key: 'comms-playbook',
			title: t`Communications guide`,
			icon: <FileText className="size-6 text-[#6b4fa3]" strokeWidth={1.5} />,
			href: pickLink(COMMS_PLAYBOOK_LINKS, variant),
			ctaLabel: t`Open document`,
		},
		{
			key: 'comms-reel-playbook',
			title: t`The ABC of an effective reel`,
			icon: <Video className="size-6 text-[#6b4fa3]" strokeWidth={1.5} />,
			href: pickLink(COMMS_REEL_PLAYBOOK_LINKS, variant),
			ctaLabel: t`Open document`,
		},
		{
			key: 'report-playbook',
			title: t`Final report guide`,
			icon: <FileText className="size-6 text-[#6b4fa3]" strokeWidth={1.5} />,
			href: pickLink(REPORT_PLAYBOOK_LINKS, variant),
			ctaLabel: t`Open document`,
		},
		{
			key: 'bring-a-partner',
			title: t`Know a company that would like to join Worldcoo? Fill out this form`,
			icon: <Users className="size-6 text-[#6b4fa3]" strokeWidth={1.5} />,
			href: BRING_A_PARTNER_FORM_LINK,
			ctaLabel: t`Open form`,
		},
	];

	return (
		<>
			<MainSectionHeader
				title={<h1 className="text-foreground text-2xl font-medium tracking-[-0.2px]">{t`Toolkit`}</h1>}
			/>

			<MainSectionContent>
				<div className="flex flex-col gap-4 p-6">
					<p className="text-muted-foreground max-w-2xl text-sm">
						{t`Useful documents to help you make the most of your collaboration with Worldcoo.`}
					</p>

					<div className="flex flex-col gap-3">
						{items.map((item) => (
							<ResourceCard
								key={item.key}
								title={item.title}
								icon={item.icon}
								href={item.href}
								ctaLabel={item.ctaLabel}
							/>
						))}
					</div>
				</div>
			</MainSectionContent>
		</>
	);
}

function ResourceCard(props: { title: string; icon: React.ReactNode; href: string; ctaLabel: string }) {
	return (
		<div className="shadow-warm flex items-center gap-4 rounded-2xl border bg-white px-6 py-5">
			<div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-[#e4d9f0]">{props.icon}</div>
			<div className="flex min-w-0 flex-1 flex-col">
				<span className="text-foreground text-base leading-[1.4] font-medium tracking-[-0.2px]">{props.title}</span>
			</div>
			<Button variant="outline-warm" size="xl" className="shrink-0 gap-2" asChild>
				<a href={props.href} target="_blank" rel="noopener noreferrer">
					<ExportIcon className="size-5" />
					<span>{props.ctaLabel}</span>
				</a>
			</Button>
		</div>
	);
}
